import Notification from '@rio-cloud/rio-uikit/Notification';
import {ChangeEvent, KeyboardEventHandler, useEffect, useRef, useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import { useSelector } from 'react-redux';

import {
  ErrorMessage,
  ImgSrc,
  InputContainer,
  MeasureText,
  SendButton,
  StyledInput,
  StyledInputWrapper,
  SuggestionOverlay,
  WarningMessage
} from "@/components/molecule/ChatInput/ChatInput.styles";
import { getLocale } from '@/configuration/lang/langSlice';
import { AUTOCOMPLETE_ACTIONS, AUTOCOMPLETE_CATEGORY, gaPush } from '@/configuration/setup/googleAnalytics';
import { useAppSelector } from '@/configuration/setup/hooks';
import { useIsDemoThread } from '@/hooks/useIsDemoThread';
import {isCurrentRoute} from "@/routes/routeHelper";
import { selectActiveThread } from '@/store/thread/threadSlice';
import { fetchSuggestions } from '@/utils/suggestions/fetchSuggestions';

type ChatInputProps = {
  isLoading: boolean;
  onSendMessage: (message: string) => void;
  onSendFile?: (file: File) => void;
};

const validationRules = {
  maxMessageLength: 500,
  allowedFileType: 'application/pdf',
  maxFileSizeMB: 5,
};

const errorMessages = {
  emptyMessage: 'chatPage.chatInput.validation.empty.error',
  messageTooLong: 'chatPage.chatInput.validation.tooLong.error',
  invalidFileType: 'chatPage.chatInput.validation.invalidType.error',
  fileTooLarge: 'chatPage.chatInput.validation.tooLarge.error',
};

const warningMessages = {
  format: 'chatPage.chatInput.warning.format',
  multipleTimePeriods: 'chatPage.chatInput.warning.multipleTimePeriods',
  longTimePeriods: 'chatPage.chatInput.warning.longTimePeriods',
  buzzwords: 'chatPage.chatInput.warning.buzzwords',
};

const checkKeyWords = {
  format: 'chatPage.chatInput.keyWords.format',
  lastWeeks: 'chatPage.chatInput.keyWords.lastWeeks',
  lastMonths: 'chatPage.chatInput.keyWords.lastMonths',
  lastYears: 'chatPage.chatInput.keyWords.lastYears',
  everyPeriod: 'chatPage.chatInput.keyWords.everyPeriod',
  monthsNames: 'chatPage.chatInput.keyWords.months',
  buzzwords: 'chatPage.chatInput.keyWords.buzzwords',
};

const ChatInput = ({ isLoading, onSendMessage, onSendFile }: ChatInputProps) => {
  const intl = useIntl();
  const activeThread = useAppSelector(selectActiveThread);
  const isOnBoardingLocation = isCurrentRoute('/onboarding');
  const isDemoThread = useIsDemoThread(activeThread?.threadId ?? null) || isOnBoardingLocation;

  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState('');
  const [warning, setWarning] = useState('');
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
  const [caretTop, setCaretTop] = useState(0);
  const [caretLeft, setCaretLeft] = useState(0);

  const inputRef = useRef<HTMLInputElement>(null);
  const measureRef = useRef<HTMLSpanElement>(null);
  const debounceTimeout = useRef<number | undefined>(undefined);
  const lastSuggestionsRef = useRef<string[]>([]);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  const [hasAcceptedSuggestion, setHasAcceptedSuggestion] = useState(false);

  const currentLanguage = useSelector(getLocale);

  const showErrorMessage = (errorMessage: string) => {
    setError(errorMessage);
    Notification.error(<span>{intl.formatMessage({ id: errorMessage })}</span>);
  };

  const validateFile = (file: File): boolean => {
    if (file.type !== validationRules.allowedFileType) {
      showErrorMessage(errorMessages.invalidFileType);
      return false;
    }
    if (file.size > validationRules.maxFileSizeMB * 1024 * 1024) {
      showErrorMessage(errorMessages.fileTooLarge);
      return false;
    }
    return true;
  };

  const sanitizeInput = (value: string) => value.replace(/[<>{}]/g, '');

  const validateInput = (value: string) => {
    if (!value.trim()) {
      showErrorMessage(errorMessages.emptyMessage);
      return false;
    }
    if (value.length > validationRules.maxMessageLength) {
      showErrorMessage(errorMessages.messageTooLong);
      return false;
    }
    return true;
  };

  const checkInput = (value: string) => {
    const formatKeyWords = intl
      .formatMessage({ id: checkKeyWords.format })
      .split(',')
      .map((item) => item.trim().toLowerCase());
    const buzzwords = intl
      .formatMessage({ id: checkKeyWords.buzzwords })
      .split(',')
      .map((item) => item.trim().toLowerCase());

    const lastWeeksKeyWordsPattern = `\\b${intl.formatMessage({ id: checkKeyWords.lastWeeks }, { weeksNumber: '((?:[2-9]|d{2,})|[a-z]+)' }).toLocaleLowerCase()}\\b`;
    const lastWeeksKeyWordsRegex = new RegExp(lastWeeksKeyWordsPattern, 'gi');

    const lastMonthsKeyWordsPattern = `\\b${intl.formatMessage({ id: checkKeyWords.lastMonths }, { monthsNumber: '((?:[2-9]|\\\\d{2,})|[a-z]+)' }).toLocaleLowerCase()}\\b`;
    const lastMonthsKeyWordsRegex = new RegExp(lastMonthsKeyWordsPattern, 'gi');

    const lastYearsKeyWordsPattern = `\\b${intl.formatMessage({ id: checkKeyWords.lastYears }, { yearsNumber: '((?:[2-9]|d{2,})|[a-z]+)' }).toLocaleLowerCase()}\\b`;
    const lastYearsKeyWordsRegex = new RegExp(lastYearsKeyWordsPattern, 'gi');

    const everyKeyWords = intl
      .formatMessage({ id: checkKeyWords.everyPeriod })
      .split(',')
      .map((item) => item.trim().toLowerCase());

    const monthsPattern = intl
      .formatMessage({ id: checkKeyWords.monthsNames })
      .split(',')
      .map((item) => item.trim().toLowerCase())
      .join('|');

    const yearPattern = new RegExp(
      `\\b\\d{4}\\b(?![-\\/\\.\\s]\\d{2}|\\s(?:${monthsPattern})|[-\\/\\.\\s]\\d{1,2}[-\\/\\.\\s]\\d{1,2}|\\s\\d{1,2}\\s(?:${monthsPattern}))`,
      'i',
    );

    // Additional regular expressions to ensure the year is not part of common date formats
    const dateRegex1 = /\b\d{1,2}[-\/\.\s]\d{1,2}[-\/\.\s]\d{4}\b/;
    const dateRegex2 = new RegExp(`\\b\\d{1,2}\\s(?:${monthsPattern})\\s\\d{4}\\b`, 'i');
    const dateRegex3 = new RegExp(`\\b(?:${monthsPattern})\\s\\d{1,2},\\s\\d{4}\\b`, 'i');
    const dateRegex4 = new RegExp(`\\b(?:${monthsPattern})\\s\\d{4}\\b`, 'i');

    const lowerValue = value.toLocaleLowerCase();

    const containsMultipleTimePeriods = () =>
      lastWeeksKeyWordsRegex.test(lowerValue) ||
      lastMonthsKeyWordsRegex.test(lowerValue) ||
      lastYearsKeyWordsRegex.test(lowerValue) ||
      everyKeyWords.some((word) => lowerValue.includes(word)) ||
      new RegExp(monthsPattern, 'i').test(lowerValue);

    const isLongTimePeriod = () =>
      yearPattern.test(lowerValue) &&
      ![dateRegex1, dateRegex2, dateRegex3, dateRegex4].some((regex) => regex.test(lowerValue));

    if (formatKeyWords.some((word) => lowerValue.includes(word))) {
      setWarning(warningMessages.format);
    } else if (buzzwords.some((word) => lowerValue.includes(word))) {
      setWarning(warningMessages.buzzwords);
    } else if (containsMultipleTimePeriods()) {
      setWarning(warningMessages.multipleTimePeriods);
    } else if (isLongTimePeriod()) {
      setWarning(warningMessages.longTimePeriods);
    } else {
      setWarning('');
    }
  };

  const handleSend = () => {
    if (validateInput(inputValue)) {
      onSendMessage(inputValue);

      if (!hasAcceptedSuggestion && lastSuggestionsRef.current.length > 0) {
        const typedSuggestionManually = lastSuggestionsRef.current.some(
          (suggestion) => suggestion.trim().toLowerCase() === inputValue.trim().toLowerCase(),
        );

        if (typedSuggestionManually) {
          gaPush({
            category: AUTOCOMPLETE_CATEGORY,
            action: AUTOCOMPLETE_ACTIONS.TYPED_MANUALLY,
            label: inputValue,
          });
        }
      }

      setWarning('');
      setInputValue('');
      setSuggestions([]);
      setActiveSuggestionIndex(-1);
      setHasAcceptedSuggestion(false);
   }
  };

  const applySuggestion = (suggestionText: string) => {
    const lines = inputValue.split('\n');
    const lastLine = lines[lines.length - 1];
    const lastWord = lastLine.trim().split(/\s+/).pop()?.toLowerCase() ?? '';

    let completion = suggestionText.trim();
    if (completion.toLowerCase().startsWith(lastWord) && lastWord.length > 0) {
      completion = completion.substring(lastWord.length);
    }

    lines[lines.length - 1] = lastLine + completion;

    const newInputValue = lines.join('\n');

    setInputValue(newInputValue);
    setSuggestions([]);
    setActiveSuggestionIndex(-1);
    setHasAcceptedSuggestion(true);

    gaPush({
      category: AUTOCOMPLETE_CATEGORY,
      action: AUTOCOMPLETE_ACTIONS.ACCEPT_SUGGESTION,
      label: suggestionText,
    });
  };

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement | HTMLTextAreaElement> = (e) => {
    if (e.key === 'Backspace' || e.shiftKey) {
      return;
    }

    if (suggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveSuggestionIndex((prevIndex) => {
          const newIndex = prevIndex < suggestions.length - 1 ? prevIndex + 1 : 0;

          gaPush({
            category: AUTOCOMPLETE_CATEGORY,
            action: AUTOCOMPLETE_ACTIONS.ARROW_NAVIGATION,
            label: `Index: ${newIndex}`,
          });

          return newIndex;
        });
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveSuggestionIndex((prevIndex) => {
          const newIndex = prevIndex > 0 ? prevIndex - 1 : suggestions.length - 1;

          gaPush({
            category: AUTOCOMPLETE_CATEGORY,
            action: AUTOCOMPLETE_ACTIONS.ARROW_NAVIGATION,
            label: `Index: ${newIndex}`,
          });

          return newIndex;
        });
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (!isLoading) {
          handleSend();
        }
      } else if (e.key === 'Tab') {
        if (activeSuggestionIndex >= 0) {
          e.preventDefault();
          applySuggestion(suggestions[activeSuggestionIndex]);
        }
      }
    } else if (e.key === 'Enter' && !isLoading) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const target = e.target as HTMLInputElement;
    const value = target.value;
    checkInput(value);

    if (target.files && target.files[0]) {
      const file = target.files[0];
      if (validateFile(file)) {
        if (onSendFile) onSendFile(file);
      }
      target.value = '';
    } else {
      const sanitizedValue = sanitizeInput(value.slice(0, validationRules.maxMessageLength));
      setInputValue(sanitizedValue);
      if (error) {
        setError('');
      }

      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }

      debounceTimeout.current = window.setTimeout(() => {
        const words = sanitizedValue.split(/\s+/);
        const lastWord = words[words.length - 1];

        const newSuggestions = fetchSuggestions(lastWord, currentLanguage || 'en-GB');

        if (newSuggestions.length > 0) {
          gaPush({
            category: AUTOCOMPLETE_CATEGORY,
            action: AUTOCOMPLETE_ACTIONS.SUGGESTIONS_SHOWN,
            label: newSuggestions.join(', '),
          });

          lastSuggestionsRef.current = newSuggestions;
        }

        setSuggestions(newSuggestions);
        setActiveSuggestionIndex(newSuggestions.length > 0 ? 0 : -1);
      }, 300);
    }
  };

  const updateCaretPosition = () => {
    const ta = textAreaRef.current;
    if (!ta) return;

    const style = getComputedStyle(ta);
    const lineHeight = parseInt(style.lineHeight || '15', 10);
    const paddingTop = parseInt(style.paddingTop || '0', 10);
    const paddingLeft = parseInt(style.paddingLeft || '0', 10);

    const textBeforeCaret = ta.value.substring(0, ta.selectionStart);
    const lines = textBeforeCaret.split("\n");

    const linesBeforeCaret = lines.length - 1;
    const currentLine = lines[lines.length - 1];

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.font = `${style.fontSize} ${style.fontFamily}`;
    const textWidth = ctx.measureText(currentLine).width;

    const caretTop = linesBeforeCaret * lineHeight + paddingTop - ta.scrollTop - 2;
    const caretLeft = textWidth + paddingLeft - ta.scrollLeft;

    setCaretTop(caretTop);
    setCaretLeft(caretLeft);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (inputRef.current && !inputRef.current.contains(event.target as Node)) {
        if (suggestions.length > 0) {
          setSuggestions([]);
          setActiveSuggestionIndex(-1);

          if (!hasAcceptedSuggestion) {
            gaPush({
              category: AUTOCOMPLETE_CATEGORY,
              action: AUTOCOMPLETE_ACTIONS.CLOSE_WITHOUT_USE,
              label: 'Click outside',
            });
          }
        }
      }
    };

    const handleRawEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && suggestions.length > 0) {
        setSuggestions([]);
        setActiveSuggestionIndex(-1);

        if (!hasAcceptedSuggestion) {
          gaPush({
            category: AUTOCOMPLETE_CATEGORY,
            action: AUTOCOMPLETE_ACTIONS.CLOSE_WITHOUT_USE,
            label: 'Escape key',
          });
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('keydown', handleRawEscape);

    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('keydown', handleRawEscape);
    };
  }, [suggestions, hasAcceptedSuggestion]);

  useEffect(() => {
    const lines = inputValue.split('\n');
    const lastLine = lines[lines.length - 1].trim();

    const words = lastLine.split(/\s+/);
    const lastWord = words[words.length - 1] || '';

    if (measureRef.current) {
      measureRef.current.textContent = lastWord;
    }

    if (textAreaRef.current) {
      const ta = textAreaRef.current;
      ta.style.height = "auto";

      const minHeight = 40;
      const maxHeight = 160;
      const newHeight = Math.min(Math.max(ta.scrollHeight, minHeight), maxHeight);

      ta.style.height = `${newHeight}px`;
    }
  }, [inputValue]);

  useEffect(() => {
    updateCaretPosition();
  }, [inputValue, activeSuggestionIndex]);

  const currentSuggestion =
    suggestions.length > 0 && activeSuggestionIndex >= 0 ? suggestions[activeSuggestionIndex] : '';

  const words = inputValue.split(/\s+/);
  const lastWord = words[words.length - 1].toLowerCase();

  let remainder = currentSuggestion.trim();
  if (currentSuggestion.toLowerCase().startsWith(lastWord) && lastWord.length > 0) {
    remainder = currentSuggestion.substring(lastWord.length);
  }

  return (
      <div>
        {warning && (
            <WarningMessage>
              <ImgSrc className="rioglyph rioglyph-info-sign padding-right-3"/>
              {intl.formatMessage({id: warning})}
            </WarningMessage>
        )}
        <InputContainer>
          <StyledInputWrapper ref={inputRef}>
            <MeasureText ref={measureRef}>{inputValue}</MeasureText>
            <StyledInput
                ref={textAreaRef}
                id="chat-input"
                rows={1}
                disabled={isDemoThread}
                value={inputValue}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                maxLength={validationRules.maxMessageLength}
                autoComplete="off"
                aria-autocomplete="list"
                aria-controls="suggestions-list"
                aria-expanded={suggestions.length > 0}
                aria-haspopup="listbox"
                data-testid="input-send-message"
                onClick={updateCaretPosition}
                onKeyUp={updateCaretPosition}
                placeholder={intl.formatMessage({id: 'chatPage.chatInput.placeholder'})}
            />
            {currentSuggestion && remainder && (
                <SuggestionOverlay
                    style={{
                      position: 'absolute',
                      left: `${caretLeft}px`,
                      top: `${caretTop}px`,
                      pointerEvents: 'none'
                    }}>
                  {remainder}
                </SuggestionOverlay>
            )}
          </StyledInputWrapper>
          <SendButton
              type="button"
              className="btn btn-primary"
              onClick={() => handleSend()}
              disabled={!inputValue.trim() || isLoading || isDemoThread}
              aria-label="Send message"
              data-testid="send-button"
          >
            <span className="padding-right-3">
              <FormattedMessage id="chatPage.chatInput.button.send"/>
            </span>
            <span className="rioglyph rioglyph-send"/>
          </SendButton>
        </InputContainer>
        {error && <ErrorMessage>{intl.formatMessage({id: error})}</ErrorMessage>}
      </div>
  );
};

export default ChatInput;
